import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMessageSquare } from "react-icons/fi";
import type { EvalCellDto } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { NumericInput } from "../../../../common/input/NumericInput";

interface Props {
  cell: EvalCellDto;
  readOnly: boolean;
  onSave: (points: number | null, comment: string | undefined) => Promise<void>;
}

export const ScoreCell = ({ cell, readOnly, onSave }: Props) => {
  const { t } = useTranslation();
  const { triggerSoft } = useApiError();
  const [points, setPoints] = useState<number | null>(cell.points ?? null);
  const comment = cell.comment ?? "";

  const commit = async (nextPoints: number | null, nextComment: string) => {
    try {
      await onSave(
        nextPoints === null ? null : Math.max(0, Math.min(10, Math.round(nextPoints))),
        nextComment || undefined,
      );
    } catch (e) {
      triggerSoft(e);
    }
  };

  if (readOnly && !cell.isAutoComputed) {
    return (
      <Flex direction="column" align="center" gap="0.5">
        <HStack spacing="1">
          <Text fontWeight="bold" color="smText" fontFamily="mono">
            {cell.points != null ? cell.points.toFixed(0) : "–"}
          </Text>
          {comment && <ReadOnlyCommentPopover comment={comment} />}
        </HStack>
        {cell.weightedPoints != null && (
          <Text fontSize="2xs" color="smMuted" fontFamily="mono">
            ({cell.weightedPoints.toFixed(1)})
          </Text>
        )}
      </Flex>
    );
  }

  if (cell.isAutoComputed) {
    return (
      <Flex direction="column" align="center" gap="0.5">
        <Text fontWeight="bold" color="smPrimary" fontFamily="mono">
          {cell.points != null ? cell.points.toFixed(1) : "–"}
        </Text>
        <Text fontSize="2xs" color="smMuted" textTransform="uppercase">
          {t("auto")}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" align="center" gap="1">
      <HStack spacing="1">
        <Box w="14">
          <NumericInput
            size="sm"
            textAlign="center"
            numericValue={points}
            onNumericChange={(v) => setPoints(v)}
            onBlur={() => commit(points, comment)}
            placeholder="–"
          />
        </Box>
        <CommentPopover
          comment={comment}
          onSave={(next) => commit(points, next)}
        />
      </HStack>
      {cell.weightedPoints != null && (
        <Text fontSize="2xs" color="smMuted" fontFamily="mono">
          ({cell.weightedPoints.toFixed(1)})
        </Text>
      )}
    </Flex>
  );
};

const ReadOnlyCommentPopover = ({ comment }: { comment: string }) => {
  const { t } = useTranslation();
  return (
    <Popover placement="bottom" isLazy>
      <PopoverTrigger>
        <IconButton aria-label={t("Comment")} icon={<FiMessageSquare />} size="xs" variant="ghost" color="smPrimary" />
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverBody>
          <Text fontSize="sm" whiteSpace="pre-wrap" color="smText">
            {comment}
          </Text>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const CommentPopover = ({ comment, onSave }: { comment: string; onSave: (next: string) => void }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(comment);
  return (
    <Popover placement="bottom" onOpen={() => setDraft(comment)}>
      <PopoverTrigger>
        <IconButton
          aria-label={t("Comment")}
          icon={<FiMessageSquare />}
          size="xs"
          variant="ghost"
          color={comment ? "smPrimary" : "smMuted"}
        />
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverBody>
          <Textarea
            size="sm"
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("Comment") ?? ""}
          />
          <Flex justify="flex-end" mt="2">
            <Button size="xs" colorScheme="teal" onClick={() => onSave(draft)}>
              {t("Save")}
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
